/**
 * Patient Search Result Card Component
 * Feature: 004-restore-missing-features
 * Task: T022
 *
 * Displays a patient search result with linked professionals info
 * Used in PatientSearchStep for linking to existing patients
 */

import { User, Calendar, Stethoscope, Link2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { PatientSearchResult } from '@/features/shared/types/patient.types'

interface PatientSearchResultCardProps {
  patient: PatientSearchResult
  onSelect: (patientId: string) => void
  isLinking?: boolean
}

export function PatientSearchResultCard({
  patient,
  onSelect,
  isLinking = false,
}: PatientSearchResultCardProps) {
  return (
    <div
      className={cn(
        'p-4 border rounded-lg bg-white hover:border-teal-300 transition-colors',
        patient.isAlreadyAssigned && 'opacity-60'
      )}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Patient Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-gray-400" />
            <h4 className="font-medium text-gray-900 truncate">{patient.full_name}</h4>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {patient.age} anos
            </span>
            {patient.condition_description && (
              <span className="flex items-center gap-1">
                <Stethoscope className="h-4 w-4" />
                {patient.condition_description}
              </span>
            )}
          </div>

          {/* Linked Professionals */}
          {patient.professionals.length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-gray-400 mb-1">Profissionais vinculados:</p>
              <div className="flex flex-wrap gap-1">
                {patient.professionals.slice(0, 3).map((prof) => (
                  <Badge key={prof.id} variant="outline" className="text-xs">
                    {prof.name}
                  </Badge>
                ))}
                {patient.professionals.length > 3 && (
                  <Badge variant="outline" className="text-xs">
                    +{patient.professionals.length - 3}
                  </Badge>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action */}
        <div className="flex-shrink-0">
          {patient.isAlreadyAssigned ? (
            <Badge variant="secondary" className="bg-green-100 text-green-700">
              Ja vinculado
            </Badge>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onSelect(patient.id)}
              disabled={isLinking}
              className="text-teal-600 border-teal-200 hover:bg-teal-50"
            >
              <Link2 className="mr-1 h-4 w-4" />
              Vincular
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
